import { Bot, MessageCircle, Send, X } from 'lucide-react'
import { FormEvent, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import type { SchoolInfo } from '../types/content'

type ChatMessage = {
  id: number
  role: 'bot' | 'user'
  text: string
  links?: Array<{ label: string; to: string }>
}

const quickPrompts = [
  'Thông tin liên hệ',
  'Xem thông báo',
  'Tài liệu học tập',
  'Lịch công tác',
]

const normalize = (value: string) =>
  value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')

const sessionKey = 'nk-chatbot-session-id'
const messagesKeyPrefix = 'nk-chatbot-messages'

const createSessionId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

const getSessionId = () => {
  if (typeof window === 'undefined') return 'server'
  const existing = window.sessionStorage.getItem(sessionKey)
  if (existing) return existing
  const next = createSessionId()
  window.sessionStorage.setItem(sessionKey, next)
  return next
}

const readSessionMessages = (key: string): ChatMessage[] | null => {
  if (typeof window === 'undefined') return null
  const raw = window.sessionStorage.getItem(key)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : null
  } catch {
    return null
  }
}

function getBotReply(input: string, site?: SchoolInfo): Omit<ChatMessage, 'id' | 'role'> {
  const text = normalize(input)
  if (text.includes('lien he') || text.includes('dia chi') || text.includes('email') || text.includes('dien thoai')) {
    return {
      text: `Bạn có thể liên hệ nhà trường qua ${site?.phone || 'số điện thoại trên website'} hoặc email ${site?.email || 'được công bố ở chân trang'}.`,
      links: [{ label: 'Mở trang liên hệ', to: '/lien-he' }],
    }
  }
  if (text.includes('thong bao') || text.includes('phu huynh')) {
    return {
      text: 'Các thông báo mới nhất của nhà trường được cập nhật tại trang Thông báo.',
      links: [{ label: 'Xem thông báo', to: '/thong-bao' }],
    }
  }
  if (text.includes('tai lieu') || text.includes('hoc lieu') || text.includes('thu vien')) {
    return {
      text: 'Bạn có thể xem hình ảnh, video và tài liệu học tập trong mục Thư viện.',
      links: [{ label: 'Tài liệu học tập', to: '/thu-vien/tai-lieu-hoc-tap' }, { label: 'Thư viện', to: '/thu-vien' }],
    }
  }
  if (text.includes('lich') || text.includes('cong tac') || text.includes('su kien')) {
    return {
      text: 'Lịch công tác và sự kiện của nhà trường được đăng theo từng ngày, có thể mở chi tiết từng lịch.',
      links: [{ label: 'Xem lịch công tác', to: '/lich-cong-tac' }],
    }
  }
  if (text.includes('tin tuc') || text.includes('bai viet') || text.includes('doan doi')) {
    return {
      text: 'Tin tức, sự kiện và hoạt động đoàn đội được cập nhật trong chuyên mục Tin tức - Sự kiện.',
      links: [{ label: 'Xem tin tức', to: '/tin-tuc' }],
    }
  }
  if (text.includes('van ban') || text.includes('cong khai') || text.includes('tai xuong')) {
    return {
      text: 'Các văn bản, công văn và tài liệu công khai được lưu trong mục Văn bản.',
      links: [{ label: 'Xem văn bản', to: '/van-ban' }],
    }
  }
  if (text.includes('gioi thieu') || text.includes('nha truong')) {
    return {
      text: 'Bạn có thể xem thông tin tổng quan, ban giám hiệu, thành tích và định hướng phát triển ở trang Giới thiệu.',
      links: [{ label: 'Giới thiệu', to: '/gioi-thieu' }, { label: 'Về nhà trường', to: '/gioi-thieu/ve-nha-truong' }],
    }
  }
  return {
    text: 'Mình có thể hỗ trợ tìm thông báo, lịch công tác, tài liệu học tập, văn bản, tin tức hoặc thông tin liên hệ. Bạn muốn xem mục nào?',
    links: [
      { label: 'Thông báo', to: '/thong-bao' },
      { label: 'Lịch công tác', to: '/lich-cong-tac' },
      { label: 'Liên hệ', to: '/lien-he' },
    ],
  }
}

export function ChatBot({ site }: { site?: SchoolInfo }) {
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')
  const [chatSessionId] = useState(getSessionId)
  const storageKey = `${messagesKeyPrefix}:${chatSessionId}`
  const initialMessages = useMemo<ChatMessage[]>(() => [
    {
      id: 1,
      role: 'bot',
      text: `Xin chào! Mình là trợ lý của ${site?.schoolName || site?.name || 'nhà trường'}. Bạn cần tìm thông tin gì?`,
    },
  ], [site?.name, site?.schoolName])
  const [messages, setMessages] = useState<ChatMessage[]>(() => readSessionMessages(storageKey) ?? initialMessages)

  useEffect(() => {
    if (messages.length === 1 && messages[0]?.id === 1) {
      setMessages(initialMessages)
    }
  }, [initialMessages])

  useEffect(() => {
    window.sessionStorage.setItem(storageKey, JSON.stringify(messages))
  }, [messages, storageKey])

  const sendMessage = (value: string) => {
    const content = value.trim()
    if (!content) return
    setMessages((current) => {
      const userMessage: ChatMessage = { id: Date.now(), role: 'user', text: content }
      const botReply = getBotReply(content, site)
      const botMessage: ChatMessage = { id: Date.now() + 1, role: 'bot', ...botReply }
      return [...current, userMessage, botMessage]
    })
    setInput('')
    setOpen(true)
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    sendMessage(input)
  }

  return (
    <div className={`chatbot ${open ? 'chatbot--open' : ''}`}>
      {open && (
        <section className="chatbot__panel" aria-label="Chatbot AI Lý Thường Kiệt">
          <div className="chatbot__header">
            <span><Bot /> Chatbot AI Lý Thường Kiệt</span>
            <button type="button" onClick={() => setOpen(false)} aria-label="Đóng chatbot"><X /></button>
          </div>
          <div className="chatbot__messages">
            {messages.map((message) => (
              <div className={`chatbot__message chatbot__message--${message.role}`} key={message.id}>
                <p>{message.text}</p>
                {message.links && (
                  <div className="chatbot__links">
                    {message.links.map((link) => <Link to={link.to} key={link.to} onClick={() => setOpen(false)}>{link.label}</Link>)}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="chatbot__quick">
            {quickPrompts.map((prompt) => <button type="button" key={prompt} onClick={() => sendMessage(prompt)}>{prompt}</button>)}
          </div>
          <form className="chatbot__form" onSubmit={submit}>
            <input value={input} onChange={(event) => setInput(event.target.value)} placeholder="Nhập câu hỏi..." />
            <button type="submit" aria-label="Gửi"><Send /></button>
          </form>
        </section>
      )}
      <button className="chatbot__toggle" type="button" onClick={() => setOpen((value) => !value)} aria-label="Mở chatbot">
        {open ? <X /> : <MessageCircle />}
      </button>
    </div>
  )
}
